import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { Cell, Row, downloadSheets, generateSheet } from '../../../utils';
import XLSX, { Range } from 'xlsx-js-style';
import {
    Row as BSRow,
    Table,
    DropdownButton,
    Dropdown,
} from 'react-bootstrap';
import { AuthedLayout } from '../../../components';
import { FluorStudentStatistic, UserInfo } from '../../../model';
import apis from '../../../apis';
import { toast } from 'react-toastify';
import { useSemGrade } from '../../../hook';


const borderStyle = { color: { rgb: '000000' }, style: 'thin' } as const;
const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;
const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;

const mapState = (state: ApplicationState) => ({ ...state.auth });
const mapDispatches = ErrorDispatches;

const connector = connect(mapState, mapDispatches);

type Props = ConnectedProps<typeof connector>;

const teeFluorStaticClass: FunctionComponent<Props> = ({ user, catchErrorForModal }) => {
    const { currentSemester = '', semesters = {} } = user || {};
    const [currentSem, setCurrentSem] = useState(currentSemester);
    const { year = -1, sem = -1 } = semesters[currentSem];
    const [datas, setDatas] = useState<FluorStudentStatistic[]>([]);
    const [userinfo, setUser] = useState<UserInfo>();
    const { yearSem, element } = useSemGrade();

    useEffect(() => {
        if (yearSem) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);

            toast
                .promise(
                    apis
                        .getFluorClassStatic(yearSem?.year, yearSem?.sem)
                        .then(r => {
                            setDatas(r);
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
        }
    }, [yearSem?.year, yearSem?.sem]);

    const gradeBase = datas.reduce((grades, d) => {
        return { ...grades, [d.grade]: [...(grades[d.grade] || []), d] };
    }, {} as Record<number, FluorStudentStatistic[]>);

    let schoolcount = 0;
    let schoolneverfluor = 0;
    let schoolyearsemdont = 0;
    let schoolfluorcont = 0;
    let schoolisfluorcount = 0;
    let schoolfluorweek = 0;
    const contentRows: Row[] = Object.values(gradeBase).flatMap(
        (gs, idx, allArray) => {
            const classFluor = {
                grade: 0,
                classNo: 0,
                neverFluor: 0,
                studentCount: 0,
                yearSemDontFluor: 0,
                fluorStudentCount: 0,
                isFluorStudentCount: 0,
                fluorWeek: 0
            };
            const rows = gs.flatMap((g, idx) => {
                return FluorStaticToRows(
                    g.grade,
                    g.classNo ?? 0,
                    g.neverFluor,
                    g.studentCount,
                    g.yearSemDontFluor,
                    g.fluorStudentCount,
                    g.isFluorStudentCount,
                    g.fluorWeek ?? 0,
                    idx === 0 ? { row: (gs.length + 1) } : undefined
                );
            });
            gs.forEach(st => {
                classFluor.neverFluor = st.neverFluor;
                classFluor.studentCount += st.studentCount;
                classFluor.yearSemDontFluor += st.yearSemDontFluor;
                classFluor.fluorStudentCount += st.fluorStudentCount;
                classFluor.isFluorStudentCount += st.isFluorStudentCount;
                classFluor.fluorWeek = classFluor.fluorWeek || (st.fluorWeek ?? 0);

                schoolneverfluor += st.neverFluor;
                schoolcount += st.studentCount;
                schoolyearsemdont += st.yearSemDontFluor;
                schoolfluorcont += st.fluorStudentCount;
                schoolisfluorcount += st.isFluorStudentCount;
                schoolfluorweek = schoolfluorweek || (st.fluorWeek ?? 0);
            });
            rows.push(...FluorStaticToRows('', '小計', classFluor.neverFluor, classFluor.studentCount, classFluor.yearSemDontFluor, classFluor.fluorStudentCount, classFluor.isFluorStudentCount, classFluor.fluorWeek));
            if (idx === allArray.length - 1) {
                const lastRow = rows[rows.length - 1];

                lastRow.cells = lastRow?.cells.map((c) => {
                    const { style: { border = {} } = {} } = c;
                    border.bottom = borderStyle;
                    c.style = { ...c.style, border };
                    return c;
                });
            }

            return rows;
        }
    );
    const content: Row[] = [
        {
            cells: [
                {
                    value: '年級班級',
                    merge: { column: 2 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '學生數',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '不參與數',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '實施週數',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '參與率',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '未實施人次',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '應實施人次',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '實施人次',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
                {
                    value: '執行率',
                    merge: { column: 1 },
                    style: { border: fullBorderStyle },
                },
            ],
        },
        ...contentRows,
        ...FluorStaticToRows('全校合計', '', schoolneverfluor, schoolcount, schoolyearsemdont, schoolfluorcont, schoolisfluorcount, schoolfluorweek, { column: 2, row: 1 }),
    ];

    const handlePrint = () => {
        const printWindow = window.open(
            '',
            '_blank',
            'width=1400,height=900'
        );

        if (!printWindow) {
            alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
            return;
        }

        const escapeHtml = (value: unknown): string => {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        };

        const title =
            `${yearSem?.year}學年第${yearSem?.sem}學期_含氟漱口水執行統計(班級)`;

        const tableRows = content
            .map((row) => {
                const cells = row.cells
                    .map((cell) => {
                        if (cell.value === undefined) {
                            return '';
                        }

                        const colSpan = cell.merge?.column
                            ? ` colspan="${cell.merge.column}"`
                            : '';

                        const rowSpan = cell.merge?.row
                            ? ` rowspan="${cell.merge.row}"`
                            : '';

                        return `
                        <td${colSpan}${rowSpan}>
                            ${escapeHtml(cell.value)}
                        </td>
                    `;
                    })
                    .join('');

                return `<tr>${cells}</tr>`;
            })
            .join('');

        printWindow.document.open();

        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="zh-Hant">
        <head>
            <meta charset="UTF-8" />
            <title>${escapeHtml(title)}</title>

            <style>
                * {
                    box-sizing: border-box;
                }

                html,
                body {
                    margin: 0;
                    padding: 0;
                    font-family:
                        "Microsoft JhengHei",
                        "Noto Sans TC",
                        Arial,
                        sans-serif;
                    color: #000;
                }

                .print-page {
                    min-height: 190mm;
                    display: flex;
                    flex-direction: column;
                }

                h1 {
                    margin: 0 0 15px;
                    text-align: center;
                    font-size: 18px;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }

                td {
                    border: 1px solid #000;
                    padding: 5px 3px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 10px;
                    word-break: break-word;
                }

                .footer {
    margin-top: auto;
    padding-top: 16px;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    width: 100%;
}

                @page {
                    size: A4 landscape;
                    margin: 8mm;
                }

                @media print {
                    body {
                        padding: 0;
                    }

                    tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                }
            </style>
        </head>

        <body>
            <div class="print-page">

                <h1>${escapeHtml(title)}</h1>

                <table>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>

                <div class="footer">
  <span>承辦人:</span>
  <span>組長:</span>
  <span>主任:</span>
  <span>校長:</span>
</div>

            </div>

            <script>
                window.onload = function () {
                    window.focus();

                    setTimeout(function () {
                        window.print();
                    }, 300);
                };

                window.onafterprint = function () {
                    window.close();
                };
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
    };

    return (
        <AuthedLayout>
            <BSRow className="justify-content-between">
                <span className="mr-3">{element}</span>
                <DropdownButton
                    id="tee-fluor-static-class-export"
                    title="匯出"
                    variant="success"
                    disabled={!datas.length}
                >
                    <Dropdown.Item
                        onClick={() => {
                            const workSheet = generateSheet(
                                [
                                    {
                                        cells: [
                                            {
                                                value:
                                                    `${yearSem?.year}學年第${yearSem?.sem}學期_含氟漱口水執行統計(班級)`,
                                                style: {
                                                    alignment: {
                                                        horizontal: 'left'
                                                    }
                                                },
                                            },
                                        ],
                                    },
                                    ...content,
                                    {
                                        cells: [
                                            {
                                                value:
                                                    '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                                style: {
                                                    alignment: {
                                                        horizontal: 'left'
                                                    }
                                                },
                                            },
                                        ],
                                    },
                                ],
                                {
                                    alignment: cellAlignStyle,
                                }
                            );

                            downloadSheets(
                                `${yearSem?.year}學年第${yearSem?.sem}學期_含氟漱口水執行統計(班級)`,
                                userinfo?.exportFileType,
                                {
                                    sheet: workSheet,
                                    name: 'st'
                                }
                            );
                        }}
                    >
                        Excel 下載
                    </Dropdown.Item>

                    <Dropdown.Item onClick={handlePrint}>
                        列印
                    </Dropdown.Item>
                </DropdownButton>
            </BSRow>
            <hr />
            <BSRow>
                <Table
                    striped
                    bordered
                    className="text-center"
                    style={{ tableLayout: 'fixed' }}
                >
                    <colgroup>
                        <col style={{ width: '3%' }} />
                        <col style={{ width: '3%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '7%' }} />
                    </colgroup>
                    <tbody>
                        {content.map((r, i) => {
                            const cells = r.cells.map((c, j) =>
                                c.value != undefined ? (
                                    <td
                                        colSpan={c.merge?.column}
                                        rowSpan={c.merge?.row}
                                        key={`${i}-${j}`}
                                    >
                                        {c.value}
                                    </td>
                                ) : (
                                    <Fragment key={`${i}-${j}`}></Fragment>
                                )
                            );
                            return <tr key={i}>{cells}</tr>;
                        })}
                    </tbody>
                </Table>
            </BSRow>
        </AuthedLayout>
    );
};

function FluorStaticToRows(
    grade: number | string,
    classNo: number | string,
    neverFluor: number,          // 不參與人數
    studentCount: number,       // 班級學生數
    yearSemDontFluor: number,   // 未實施人次（次數加總）
    fluorStudentCount: number,  // 應實施人次（後端已算好也可重算）
    isFluorStudentCount: number,// 實施人次
    fluorWeek: number | string,
    gradeMerge?: { row?: number; column?: number }
): Row[] {

    const week = Number(fluorWeek) || 0;

    const notJoin = neverFluor;                      // 不參與數（人）
    const shouldDo = (studentCount - notJoin) * week; // 應實施人次
    const notDone = yearSemDontFluor;                // 未實施人次
    const done = shouldDo - notDone;                 // 實施人次
    const joinRate = percent(studentCount - notJoin, studentCount);
    const execRate = percent(done, shouldDo);

    const gradeNo = [
        {
            value: gradeMerge ? grade : undefined,
            merge: gradeMerge,
            style: { border: fullBorderStyle },
        },
    ];

    if (!gradeMerge?.column) {
        gradeNo.push({
            value: classNo,
            merge: { row: 1 },
            style: { border: fullBorderStyle },
        });
    }

    return [
        {
            cells: [
                ...gradeNo,
                { value: studentCount },
                { value: notJoin },        // 不參與數（人）
                { value: week },           // 實施週數
                { value: joinRate },       // 參與率
                { value: notDone },        // 未實施人次（次數）
                { value: shouldDo },       // 應實施人次
                { value: done },           // 實施人次
                { value: execRate },       // 執行率
            ],
        },
    ];
}


function percent(value: number, denominator: number): string {
    if (!denominator) return '0.00%';
    return ((value / denominator) * 100).toFixed(2) + '%';
}

export const TeeFluorStaticClass = connector(teeFluorStaticClass);
